import React from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Edit, Trash2, Settings, Trophy, Clock, Users } from 'lucide-react';
import { Quiz } from '@/types/quiz';

interface QuizTableProps {
  quizzes: Quiz[];
  onEdit: (quiz: Quiz) => void;
  onDelete: (quizId: string) => void;
  onManageQuestions: (quiz: Quiz) => void;
}

export const QuizTable = ({ quizzes, onEdit, onDelete, onManageQuestions }: QuizTableProps) => {
  const getDifficultyBadge = (difficulty: number) => {
    const configs = {
      1: { label: '⭐ Beginner', variant: 'secondary' as const },
      2: { label: '⭐⭐ Easy', variant: 'outline' as const },
      3: { label: '⭐⭐⭐ Medium', variant: 'default' as const },
      4: { label: '⭐⭐⭐⭐ Hard', variant: 'destructive' as const },
      5: { label: '⭐⭐⭐⭐⭐ Expert', variant: 'destructive' as const },
    };
    return configs[difficulty as keyof typeof configs] || configs[1];
  };

  return (
    <div className="space-y-4">
      {quizzes.map((quiz) => {
        const difficultyConfig = getDifficultyBadge(quiz.difficulty);
        
        return (
          <Card key={quiz.id} className="hover:shadow-lg transition-shadow">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold">{quiz.title}</h3>
                    <Badge variant={difficultyConfig.variant}>
                      {difficultyConfig.label}
                    </Badge>
                    <Badge variant={quiz.is_active ? 'default' : 'secondary'}>
                      {quiz.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </div>
                  
                  {quiz.description && (
                    <p className="text-muted-foreground mb-3">{quiz.description}</p>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Trophy className="h-4 w-4" />
                      <span>{quiz.points_reward} points</span>
                    </div>
                    
                    {quiz.time_limit && (
                      <div className="flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        <span>{quiz.time_limit} min</span>
                      </div>
                    )}
                    
                    <div className="flex items-center gap-1">
                      <Users className="h-4 w-4" />
                      <span>{quiz.attempts_allowed} attempts</span>
                    </div>
                    
                    <span className="text-xs">
                      Created {new Date(quiz.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onManageQuestions(quiz)}
                    className="text-blue-600 hover:text-blue-700"
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Questions
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onEdit(quiz)}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => onDelete(quiz.id)}
                    className="text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};